import { IconCalendarDollar, IconCalendarTime, IconExternalLink } from "@tabler/icons-react";
import { useNavigate } from "@tanstack/react-router";
import dayjs from "dayjs";
import { Card, Divider, Group, IconAction, Stack, Text } from "@/components/ui";
import type { PagoProgramado } from "../services/calendario-pagos.api";
import { SemaforoPagoBadge } from "./SemaforoPago";

const FMT = new Intl.NumberFormat("es-MX", { minimumFractionDigits: 2 });

/**
 * Panel lateral del calendario (P39) · detalle COMPACTO de los pagos del día
 * seleccionado, en cards apiladas (la tabla completa vive en la vista Tabla).
 */
export function PagosDelDiaPanel({
  dia,
  pagos,
  onReprogramar,
}: {
  dia: string | null;
  pagos: PagoProgramado[];
  /** Ausente en solo lectura (Visor) · sin él no se muestra el botón de reprogramar. */
  onReprogramar?: (pago: PagoProgramado) => void;
}) {
  const navigate = useNavigate();

  return (
    <Card padding="md" className="h-full">
      <Stack gap="sm">
        <Group gap={8}>
          <IconCalendarDollar size={18} stroke={1.7} />
          <Text fw={600} size="sm">
            {dia ? `Pagos del ${dayjs(dia).format("DD [de] MMMM")}` : "Pagos del día"}
          </Text>
        </Group>
        <Divider />

        {!dia ? (
          <Text size="sm" c="dimmed">
            Selecciona un día del calendario para ver el detalle de sus pagos.
          </Text>
        ) : pagos.length === 0 ? (
          <Text size="sm" c="dimmed">
            Sin pagos programados este día.
          </Text>
        ) : (
          pagos.map((p) => (
            <Card key={p.solicitudId} padding="sm">
              <Stack gap={6}>
                <Group justify="space-between" className="flex-nowrap">
                  <div className="min-w-0">
                    <Text size="sm" fw={600} truncate className="font-mono">
                      {p.folio}
                    </Text>
                    <Text size="xs" c="dimmed" truncate>
                      {p.organizacionNombre ?? "—"}
                    </Text>
                  </div>
                  <SemaforoPagoBadge estado={p.estado} />
                </Group>

                <div>
                  <Text size="sm" className="line-clamp-1" title={p.proveedorRazonSocial ?? undefined}>
                    {p.proveedorRazonSocial ?? "—"}
                    {p.proveedorNumero ? ` · ${p.proveedorNumero}` : ""}
                  </Text>
                  {p.descripcion && (
                    <Text size="xs" c="dimmed" className="line-clamp-1" title={p.descripcion}>
                      {p.descripcion}
                    </Text>
                  )}
                </div>

                <Group justify="space-between" align="center">
                  <div>
                    <Text size="sm" fw={700} className="whitespace-nowrap">
                      ${FMT.format(p.importe)} {p.moneda ?? ""}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {p.cantidadCfdis} CFDI{p.cantidadCfdis === 1 ? "" : "s"} · {p.diasProgramacion} días de programación
                    </Text>
                  </div>
                  <Group gap={2} className="flex-nowrap">
                    {onReprogramar && (
                      <IconAction label="Reprogramar fecha de pago" size="sm" onClick={() => onReprogramar(p)}>
                        <IconCalendarTime size={16} stroke={1.7} />
                      </IconAction>
                    )}
                    <IconAction
                      label="Ver solicitud"
                      size="sm"
                      onClick={() => void navigate({ to: "/solicitudes/$id", params: { id: p.solicitudId } })}
                    >
                      <IconExternalLink size={16} stroke={1.7} />
                    </IconAction>
                  </Group>
                </Group>
              </Stack>
            </Card>
          ))
        )}
      </Stack>
    </Card>
  );
}
